import type { CandidateFile } from 'src/models';
import { getResizedImage } from 'src/lib/imageHelper';

// mime-types の lookup() はブラウザ環境で動作しないため、lookup() 相当を独自実装
const getFileExtension = (filePath: string): string => {
  const lastDot = filePath.lastIndexOf('.');
  if (lastDot === -1) return '';
  return filePath.slice(lastDot + 1).toLowerCase();
};

export interface FileObj extends CandidateFile {
  file: File;
}

export async function convertFilesToFileObjs(fileList: FileList, maxId: number, limitMB: number): Promise<FileObj[]> {
  if (fileList.length === 0) { return []; }
  const fileObjs: FileObj[] = [];

  for (let i = 0; i < fileList.length; i++) {
    const file = fileList[i];
    if (file.type.includes('image/')) {
      await addImageFileObj(fileObjs, file, maxId + i + 1);
    } else {
      checkFileSize(file, limitMB);
      addGeneralFileObj(fileObjs, file, maxId + i + 1);
    }
  }
  return fileObjs;
}

export async function addImageFileObj(fileObjs: FileObj[], file: File, id: number): Promise<void> {
  // リサイズ後は確実に10MBを切るのでサイズ確認はしなくてよい.
  // 動画プレイヤーの付箋追加で切り出される1920x1080の画像を許容するため、2000x2000にリサイズする.
  const resizedImage = await getResizedImage(file, { maxWidth: 2000, maxHeight: 2000 });
  if (!resizedImage) return;
  fileObjs.push({
    id,
    content: URL.createObjectURL(resizedImage),
    type: file.type,
    name: file.name,
    path: file.name,
    file: new File([resizedImage], file.name, { type: file.type }),
    selected: false,
  });
}

export function addGeneralFileObj(fileObjs: FileObj[], file: File, id: number): void {
  fileObjs.push({
    id,
    content: URL.createObjectURL(file),
    type: file.type,
    name: file.name,
    path: file.name,
    file,
    selected: true,
  });
}

export function checkFileSize(file: File, limitMB: number): void {
  const fileSize = file.size;
  const limitSize = limitMB * 1024 * 1024;
  if (fileSize > limitSize) {
    throw new Error(`添付ファイルの最大サイズは${limitMB}MBです。`);
  }
}

const EXT_ICON_MAP: Record<string, string> = {
  pdf: 'fa-file-pdf',
  doc: 'fa-file-word',
  docx: 'fa-file-word',
  xls: 'fa-file-excel',
  xlsx: 'fa-file-excel',
  ppt: 'fa-file-powerpoint',
  pptx: 'fa-file-powerpoint',
};

export function fileTypeIconFromFilePath(filePath: string): string {
  const ext = getFileExtension(filePath);
  return EXT_ICON_MAP[ext] ?? 'fa-file';
}

export function isImageFileType(filePath: string): boolean {
  const ext = getFileExtension(filePath);
  return ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(ext);
}
